import React from 'react';
import { Screen, AppState } from '../App';
import { User, getDaysLeft, getStudents, getPendingCheckIns, getStrategiesNeedingRatingToday } from '../storage';
import { roleLabels } from '../roleLabels';
import * as authApi from '../authApi';
import NavBar from '../components/NavBar';
import GuidedTour, { TourStep } from '../components/GuidedTour';

interface Props { navigate: (s: Screen, state?: AppState) => void; user: User | null; }

export default function Dashboard({ navigate, user }: Props) {
  const L = roleLabels(user);
  const isFriday = new Date().getDay() === 5;
  const [showMenu, setShowMenu] = React.useState(false);
  const daysLeft = user ? getDaysLeft(user) : 7;

  // One-time nudge to rate the app, shown only in the last couple of trial days
  // and never again once she's been prompted (records a per-device flag).
  const [showFeedbackNudge, setShowFeedbackNudge] = React.useState(false);
  React.useEffect(() => {
    try {
      const prompted = localStorage.getItem('dms_feedback_prompted');
      if (!prompted && user && !user.isPaid && getDaysLeft(user) <= 2) setShowFeedbackNudge(true);
    } catch { /* ignore */ }
  }, [user]);
  const dismissNudge = () => { try { localStorage.setItem('dms_feedback_prompted', '1'); } catch { /* ignore */ } setShowFeedbackNudge(false); };

  // First-run guided tour: the welcome slides set the tone, but a new teacher
  // still lands on the dashboard not knowing where to tap. Show the tour once
  // (per device), and let anyone replay it from the account menu.
  const [showTour, setShowTour] = React.useState(false);
  React.useEffect(() => {
    try { if (!localStorage.getItem('dms_tour_done')) setShowTour(true); } catch { /* ignore */ }
  }, []);
  const endTour = () => { try { localStorage.setItem('dms_tour_done', '1'); } catch { /* ignore */ } setShowTour(false); };
  const startTour = () => { setShowMenu(false); setShowTour(true); };

  const tourSteps: TourStep[] = [
    { selector: null, emoji: '👋', title: `Welcome, ${user?.firstName}!`, body: "Here's a 30-second tour so you know your way around. You can replay it anytime from the menu in the top-right corner." },
    { selector: '[data-tour="urgency"]', emoji: '🆘', title: L.urgencyTitle, body: 'In a heated moment, tap here for one calm, specific thing to try right now.' },
    { selector: '[data-tour="action-talk"]', emoji: '💬', title: L.talk, body: `This is the heart of it. Enter a ${L.subject}'s ${L.isParent ? 'first name' : 'initials'} and grade, describe what's happening, and you'll get a clear breakdown — what's going on, what to try, and how to talk about it.` },
    { selector: '[data-tour="action-students"]', emoji: '👥', title: L.students, body: `Every ${L.subject} you talk about is saved here. Come back anytime to pick up where you left off — or finish any section you didn't get to.` },
    { selector: '[data-tour="quick-actions"]', emoji: '📚', title: 'Prep and learn', body: `${L.communication} helps you get ready for a hard conversation. The Brain Science Library explains what's happening — in plain language, no jargon.` },
    { selector: '[data-tour="wellness"]', emoji: '💚', title: 'We check on you, too', body: "This work is heavy. Tell us how you're doing — your wellbeing is part of this, not an afterthought." },
    { selector: null, emoji: '🌱', title: "You're all set", body: `Start by tapping "${L.talk}". You've got this — and we're here the whole way.` },
  ];

  const endsDate = user?.subscriptionEndsAt
    ? new Date(user.subscriptionEndsAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const students = user ? getStudents(user.id) : [];
  const recent = students.slice(-3).reverse();
  const pendingCheckIns = user ? getPendingCheckIns(user.id) : [];
  const needsRatingToday = user ? getStrategiesNeedingRatingToday(user.id) : [];
  const isAfternoon = new Date().getHours() >= 12;

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  };

  return (
    <div>
      <div className="top-bar">
        <div style={{display:'flex',alignItems:'center',gap:8}}><img src='/logo.png' alt='logo' style={{width:28,height:28,borderRadius:6}}/><span className="top-bar-logo">Decode My Student</span></div>
        <div className="top-bar-actions">
          {!user?.isPaid && daysLeft <= 1 && <span className="badge badge-coral">Trial ends soon</span>}
          <div style={{position:'relative'}}>
            <div className="avatar" onClick={()=>setShowMenu(!showMenu)} style={{cursor:'pointer'}}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            {showMenu && (
              <div style={{position:'absolute',right:0,top:44,background:'white',borderRadius:12,boxShadow:'var(--shadow-lg)',padding:'8px',minWidth:180,zIndex:200}}>
                <div style={{padding:'10px 14px',borderBottom:'1px solid #f0ece6'}}>
                  <p style={{fontWeight:600,fontSize:14}}>{user?.firstName} {user?.lastName}</p>
                  <p style={{fontSize:12,color:'var(--text-secondary)',marginTop:2}}>{user?.email}</p>
                </div>
                <button onClick={startTour} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  👋 Take the tour
                </button>
                <button onClick={()=>navigate('coming-soon')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  🚀 What's Coming
                </button>
                <button onClick={()=>navigate('burnout')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  💚 {L.burnout}
                </button>
                <button onClick={()=>navigate('friday')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  📅 {L.friday}
                </button>
                {!user?.isPaid && !user?.everPaid && (
                  <button onClick={()=>navigate('paywall')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--teal)',fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
                    💳 Subscribe
                  </button>
                )}
                {user?.everPaid && (
                  <button onClick={()=>navigate('billing')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                    💳 Manage subscription
                  </button>
                )}
                <button onClick={()=>navigate('feedback')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  💬 Send feedback
                </button>
                <button onClick={()=>navigate('account')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                  ⚙️ Account settings
                </button>
                {user?.isAdmin && (
                  <button onClick={()=>navigate('admin')} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--charcoal)',display:'flex',alignItems:'center',gap:8}}>
                    🏫 School Licenses
                  </button>
                )}
                <div style={{borderTop:'1px solid #f0ece6',marginTop:4}}>
                  <button onClick={async ()=>{await authApi.logout();navigate('landing');}} style={{width:'100%',padding:'10px 14px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:14,color:'var(--coral)',display:'flex',alignItems:'center',gap:8}}>
                    ← Log Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* One-time trial-end feedback nudge */}
        {showFeedbackNudge && (
          <div style={{ background: 'var(--teal-light)', border: '1px solid var(--teal)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 14 }}>💬 How's it going so far?</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>
                You're near the end of your trial. A quick rating helps us make Decode My Student better for you.
              </p>
              <button onClick={() => { dismissNudge(); navigate('feedback'); }} style={{ marginTop: 10, background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Rate the app
              </button>
            </div>
            <button onClick={dismissNudge} aria-label="Dismiss" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 20, lineHeight: 1, padding: 4, alignSelf: 'flex-start' }}>×</button>
          </div>
        )}

        {/* Trial warning */}
        {!user?.isPaid && daysLeft === 1 && (
          <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 14 }}>
            ⏰ Your free trial ends tomorrow. <span style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('paywall')}>Subscribe to keep access →</span>
          </div>
        )}

        {/* Strategy check-in banner */}
        {pendingCheckIns.length > 0 && (
          <div onClick={() => navigate('strategy-checkin')} style={{ background: 'var(--teal)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>📊 Strategy Check-In Ready</p>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                {pendingCheckIns.length} strateg{pendingCheckIns.length !== 1 ? 'ies' : 'y'} ready for follow-up — how did it go?
              </p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><polyline points="9,18 15,12 9,6" /></svg>
          </div>
        )}

        {/* Daily rating banner - shows after noon if strategies need rating */}
        {needsRatingToday.length > 0 && isAfternoon && (
          <div onClick={() => navigate('daily-rating')} style={{ background: 'var(--green)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>⭐ Rate Today's Strategies</p>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                {needsRatingToday.length} strateg{needsRatingToday.length !== 1 ? 'ies' : 'y'} to rate — takes 30 seconds
              </p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><polyline points="9,18 15,12 9,6" /></svg>
          </div>
        )}

        {/* Friday banner */}
        {isFriday && (
          <div onClick={() => navigate('friday')} style={{ background: 'var(--amber)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ color: 'white', fontWeight: 700, fontSize: 15 }}>🎉 It's Friday!</p>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>Take 2 minutes for your weekly debrief</p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" width="20" height="20"><polyline points="9,18 15,12 9,6" /></svg>
          </div>
        )}

        {/* Greeting */}
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22 }}>{greeting()}, {user?.firstName}.</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 }}>
            {students.length === 0 ? `Ready to support your first ${L.subject} today?` : `Supporting ${students.length} ${students.length !== 1 ? L.subjectPlural : L.subject}.`}
          </p>
        </div>

        {/* Always-available subscribe path for anyone not yet paid (trial or expired). */}
        {!user?.isPaid && (
          <div onClick={() => navigate('paywall')} style={{ background: 'var(--teal-light)', border: '1px solid var(--teal)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <p style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 14 }}>💳 Subscribe to Decode My Student</p>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>$29/mo or $299/year. Subscribe any time — your access continues without a gap.</p>
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--teal)" strokeWidth="2" width="20" height="20"><polyline points="9,18 15,12 9,6" /></svg>
          </div>
        )}

        {/* Subscription set to cancel: still active, but ending on a known date. */}
        {user?.isPaid && user?.cancelAtPeriodEnd && endsDate && (
          <div style={{ background: 'var(--amber-light)', border: '1px solid var(--amber)', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
            <p style={{ fontWeight: 700, color: '#b87800', fontSize: 14 }}>Your plan ends on {endsDate}</p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              You'll keep full access until then. Changed your mind?{' '}
              <span onClick={() => navigate('billing')} style={{ color: 'var(--teal)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>Manage your subscription</span>.
            </p>
          </div>
        )}

        {/* Urgency button */}
        <button data-tour="urgency" onClick={() => navigate('urgency')} className="pulse" style={{ background: 'var(--coral)', color: 'white', width: '100%', borderRadius: 14, padding: '20px', marginBottom: 16, textAlign: 'left', boxShadow: 'var(--shadow-md)', border: 'none', cursor: 'pointer' }}>
          <p style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{L.urgencyTitle}</p>
          <p style={{ fontSize: 13, opacity: 0.9 }}>{L.urgencySub}</p>
        </button>

        {/* Quick actions */}
        <div className="card-grid" data-tour="quick-actions" style={{ marginBottom: 16 }}>
          {[
            { s: 'talk' as Screen, icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />, title: L.talk, bg: 'var(--teal-light)', color: 'var(--teal)' },
            { s: 'students' as Screen, icon: <><circle cx="9" cy="7" r="4" /><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" /></>, title: L.students, bg: 'var(--teal-light)', color: 'var(--teal)' },
            { s: 'communication' as Screen, icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.43a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />, title: L.communication, bg: 'var(--amber-light)', color: 'var(--amber)' },
            { s: 'library' as Screen, icon: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></>, title: 'Brain Science Library', bg: 'var(--teal-light)', color: 'var(--teal)' },
          ].map(({ s, icon, title, bg, color }) => (
            <div key={s} data-tour={`action-${s}`} className="action-card" onClick={() => navigate(s)}>
              <div className="action-card-icon" style={{ background: bg, color }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">{icon}</svg>
              </div>
              <span className="action-card-title">{title}</span>
            </div>
          ))}
        </div>

        {/* Wellness strip */}
        <div data-tour="wellness" className="card" style={{ borderLeft: '4px solid var(--amber)', marginBottom: 16 }}>
          <p style={{ fontWeight: 600, marginBottom: 10 }}>How are you doing today, {user?.firstName}?</p>
          <div style={{ display: 'flex', gap: 8 }}>
            {['😔 Struggling', '😐 Okay', '😊 Great'].map(s => (
              <button key={s} onClick={() => navigate('burnout')} style={{ flex: 1, padding: '10px 4px', background: '#f0ece6', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}>{s}</button>
            ))}
          </div>
        </div>

        {/* Recent students */}
        {recent.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p style={{ fontWeight: 600, fontSize: 15 }}>Recent {L.isParent ? 'Kids' : 'Students'}</p>
              <span onClick={() => navigate('students')} style={{ fontSize: 13, color: 'var(--teal)', cursor: 'pointer', fontWeight: 600 }}>View all</span>
            </div>
            {recent.map(s => (
              <div key={s.id} className="card" style={{ marginBottom: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => navigate('student-profile', { currentStudentId: s.id })}>
                <div>
                  <span className="mono" style={{ fontSize: 16, fontWeight: 500, color: 'var(--teal)' }}>{s.initials}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', marginLeft: 8 }}>{s.grade}</span>
                </div>
                <span className={`trend trend-${s.trend}`}>{s.trend === 'improving' ? '↑ Improving' : s.trend === 'stable' ? '→ Stable' : '↓ Needs attention'}</span>
              </div>
            ))}
          </div>
        )}

        {students.length === 0 && (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>🌱</p>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Start with a {L.subject}</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>Tap "{L.talk}" to decode your first {L.subject} situation.</p>
            <button onClick={() => navigate('talk')} className="btn-primary">{L.talk}</button>
          </div>
        )}
      </div>

      <NavBar navigate={navigate} active="dashboard" />
      {showTour && <GuidedTour steps={tourSteps} onClose={endTour} />}
    </div>
  );
}
